import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { useDispatch, useSelector } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { RootState } from "../../../store";
import { toast } from "react-toastify";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
  zp_lgd_code: number;
  zp_name: string;
  total_bps: number;
  bps_with_mom_generated: number;
  total_meetings_uploaded: number;
  draft_mom: number;
  final_submitted_mom: number;
}

const BlockDashboard2: React.FC<{ finYear: string; meetingType: any }> = ({
  finYear,
  meetingType,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<MeetingData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const blockData = useSelector((state: RootState) => state.common.dashboardData["Block Panchayats"]);

  useEffect(() => {
    const fetchZillaAggregates = async () => {
      setTableData([]);
      if (!blockData?.state_code) return;
      try {
        const response = await axios.get(`${baseUrl}api/bp_report/${blockData.state_code}`, {
          params: { fin_year: finYear, meeting_type: meetingType.value !== "0" ? meetingType.value : undefined },
        });
        if (response?.data?.msg_code === 400) {
          toast.error(response?.data?.msg || "Something went wrong");
          return;
        }
        const mappedData: MeetingData[] = response?.data?.data?.map((item: any) => ({
          zp_lgd_code: item.zp_lgd_code,
          zp_name: item.zp_name,
          total_bps: item.total_bps,
          bps_with_mom_generated: item.bps_with_mom_generated,
          total_meetings_uploaded: item.total_meetings_uploaded,
          draft_mom: item.draft_mom || 0,
          final_submitted_mom: item.final_submitted_mom || 0,
        })) || [];
        setTableData(mappedData);
      } catch (error) {
        console.error("Error fetching ZP-level block data:", error);
      }
    };
    fetchZillaAggregates();
  }, [finYear, meetingType, baseUrl, blockData?.state_code]);

  const handleMomHighlight = (zpCode: number, zpName: string) => {
    dispatch(updateDashboardFields({ menu: "Block Panchayats", fields: { zp_lgd_code: zpCode, zp_name: zpName } }));
  };

  const handleBack = () => {
    dispatch(updateDashboardFields({ menu: "Block Panchayats", fields: { state_code: null, state_name: "", zp_lgd_code: null, zp_name: "" } }));
  };

  const columns: TableColumn<MeetingData>[] = [
    { name: "S.No.", cell: (_row, index) => (index + 1 + (currentPage - 1) * rowsPerPage).toString(), width: "80px" },
    {
      name: "ZP Name",
      selector: (row) => row.zp_name,
      cell: (row) => (
        <span className={row.total_meetings_uploaded > 0 ? "highlight-td" : ""}
          style={{ cursor: row.total_meetings_uploaded > 0 ? "pointer" : "default" }}
          onClick={() => row.total_meetings_uploaded > 0 && handleMomHighlight(row.zp_lgd_code, row.zp_name)}>
          {row.zp_name}
        </span>
      ), sortable: true,
    },
    { name: "Total BPs & Equivalents", selector: (row) => row.total_bps, sortable: true },
    { name: "BPs with MoM Generated", selector: (row) => row.bps_with_mom_generated, sortable: true },
    { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
    { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
    { name: "Total Meetings Uploaded", selector: (row) => row.total_meetings_uploaded, sortable: true },
  ];

  const filteredData = tableData.filter((item) => item.zp_name?.toLowerCase().includes(filterText.toLowerCase()) || String(item.zp_lgd_code).includes(filterText));

  const t = {
    bps: filteredData.reduce((s, r) => s + r.total_bps, 0),
    gen: filteredData.reduce((s, r) => s + r.bps_with_mom_generated, 0),
    draft: filteredData.reduce((s, r) => s + r.draft_mom, 0),
    final: filteredData.reduce((s, r) => s + r.final_submitted_mom, 0),
    upld: filteredData.reduce((s, r) => s + r.total_meetings_uploaded, 0),
  };

  const exportExcel = () => {
    const header = ["S.No.", "ZP Name", "Total BPs", "BPs with MoM", "Draft MoM", "Final Submitted", "Total Meetings"];
    const data = filteredData.map((r, i) => [i + 1, r.zp_name, r.total_bps, r.bps_with_mom_generated, r.draft_mom, r.final_submitted_mom, r.total_meetings_uploaded]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data, ["", "Total", t.bps, t.gen, t.draft, t.final, t.upld]]);
    ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BlockReport");
    XLSX.writeFile(wb, "BlockReport.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex gap-2"> 
          <button className="back-btn me-2" onClick={handleBack}>&larr; Back</button>
          <span className="data-chip">{blockData?.state_name} - {blockData?.state_code}</span>
        </div>
        <button className="shiny-btn" onClick={exportExcel}><FaFileExcel /> Export Excel</button>
      </div>
      <input type="text" className="form-control w-25 mb-2" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
      <DataTable columns={columns} data={filteredData} pagination onChangePage={setCurrentPage} onChangeRowsPerPage={setRowsPerPage} highlightOnHover striped />
      {filteredData.length > 0 && (
        <div className="table-footer fw-bold bg-light d-flex p-2 border">
          <div style={{ width: "80px" }}></div>
          <div style={{ flex: 1 }}>Total</div>
          <div style={{ flex: 1 }}>{t.bps}</div>
          <div style={{ flex: 1 }}>{t.gen}</div>
          <div style={{ flex: 1 }}>{t.draft}</div>
          <div style={{ flex: 1 }}>{t.final}</div>
          <div style={{ flex: 1 }}>{t.upld}</div>
        </div>
      )}
    </div>
  );
};

export default BlockDashboard2;